'use client';

import { useMemo, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

type Gender = 'male' | 'female';

// ACE body-fat-percentage categories, the standard published reference ranges.
const CATEGORIES: Record<Gender, Array<{ max: number; label: string }>> = {
  male: [
    { max: 5, label: 'Essential fat' },
    { max: 13, label: 'Athletes' },
    { max: 17, label: 'Fitness' },
    { max: 24, label: 'Average' },
    { max: Infinity, label: 'Obese' },
  ],
  female: [
    { max: 13, label: 'Essential fat' },
    { max: 20, label: 'Athletes' },
    { max: 24, label: 'Fitness' },
    { max: 31, label: 'Average' },
    { max: Infinity, label: 'Obese' },
  ],
};

function categoryFor(gender: Gender, pct: number) {
  return CATEGORIES[gender].find((c) => pct <= c.max)?.label ?? 'Obese';
}

export default function BrowserBodyFatCalculator() {
  const fired = useRef(false);
  const fireUsage = () => {
    if (fired.current) return;
    fired.current = true;
    void consumeToolUsage('body-fat-calculator', '').catch(() => undefined);
  };

  const [gender, setGender] = useState<Gender>('male');
  const [heightIn, setHeightIn] = useState(70);
  const [neckIn, setNeckIn] = useState(15);
  const [waistIn, setWaistIn] = useState(34);
  const [hipIn, setHipIn] = useState(38);

  function onChange<T>(setter: (v: T) => void) {
    return (v: T) => {
      fireUsage();
      setter(v);
    };
  }

  const result = useMemo(() => {
    // US Navy circumference method, the standard equipment-free estimate.
    let pct: number;
    if (gender === 'male') {
      const diff = waistIn - neckIn;
      pct =
        diff > 0
          ? 495 / (1.0324 - 0.19077 * Math.log10(diff) + 0.15456 * Math.log10(heightIn)) - 450
          : 0;
    } else {
      const diff = waistIn + hipIn - neckIn;
      pct =
        diff > 0
          ? 495 / (1.29579 - 0.35004 * Math.log10(diff) + 0.221 * Math.log10(heightIn)) - 450
          : 0;
    }
    pct = Math.max(0, Math.min(60, pct));
    return { pct, category: categoryFor(gender, pct) };
  }, [gender, heightIn, neckIn, waistIn, hipIn]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Fitness</span>
        <h2 className="tool-live-title">Body fat calculator</h2>
        <p className="tool-live-subtitle">
          Enter a few tape-measure circumferences to estimate body fat percentage using the US Navy
          method, no scale or calipers needed.
        </p>
      </div>

      <div className="tool-live-grid">
        <section
          className="tool-live-uploader"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}
        >
          <label className="tool-live-field">
            <span>Sex</span>
            <select onChange={(e) => onChange(setGender)(e.target.value as Gender)} value={gender}>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </label>
          <label className="tool-live-field">
            <span>Height (in)</span>
            <input
              min={0}
              onChange={(e) => onChange(setHeightIn)(Number(e.target.value) || 0)}
              type="number"
              value={heightIn}
            />
          </label>
          <label className="tool-live-field">
            <span>Neck (in)</span>
            <input
              min={0}
              onChange={(e) => onChange(setNeckIn)(Number(e.target.value) || 0)}
              step={0.1}
              type="number"
              value={neckIn}
            />
          </label>
          <label className="tool-live-field">
            <span>Waist (in)</span>
            <input
              min={0}
              onChange={(e) => onChange(setWaistIn)(Number(e.target.value) || 0)}
              step={0.1}
              type="number"
              value={waistIn}
            />
          </label>
          {gender === 'female' ? (
            <label className="tool-live-field">
              <span>Hip (in)</span>
              <input
                min={0}
                onChange={(e) => onChange(setHipIn)(Number(e.target.value) || 0)}
                step={0.1}
                type="number"
                value={hipIn}
              />
            </label>
          ) : null}
        </section>

        <section className="tool-live-preview">
          <p className="tool-live-panel-note">
            <strong>{result.pct.toFixed(1)}%</strong>
            <span>Estimated body fat, category: {result.category}</span>
          </p>
          <p className="tool-live-panel-note">
            Measure at the narrowest point of the neck, at the navel for the waist, and at the
            widest point of the hips (women). The US Navy method is an estimate, typically within a
            few percentage points of a DEXA scan; it is not a medical or diagnostic measurement.
          </p>
        </section>
      </div>
    </div>
  );
}
